import { useEffect, useState } from 'react';
import { Link, useLoaderData } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getCampaign, getCampaignLeaderboard } from '../../api/challenges';
import { demoCheckpoints, demoMedals } from '../../api/mockData';
import { buildMapJourney } from '../../api/mapJourney';
import { buildRelayModel } from '../../api/relay';
import { resolveCheckpoints, resolveMedals, resolveRoute } from '../../api/checkpointResolution';
import CampaignRegistrationPanel from '../campaign/CampaignRegistrationPanel';
import ChallengeStrip from './ChallengeStrip';
import CheckpointDrawer from './CheckpointDrawer';
import JourneySections from './JourneySections';
import MapOverlays from './MapOverlays';
import RelayRoute from './RelayRoute';
import StreetJourneyMap from './StreetJourneyMap';
import './RelayMap.css';

export async function loader({ params }) {
  const campaign = await getCampaign(params.campaignId);
  return { campaign, id: params.campaignId, name: params.name };
}

export default function CampaignRelayMap() {
  const { campaign, id, name } = useLoaderData();
  const { user, isLoggedIn } = useAuth();
  const [relay, setRelay] = useState(null);
  const [status, setStatus] = useState('loading');
  const [selectedCheckpoint, setSelectedCheckpoint] = useState(null);

  useEffect(() => {
    let ignore = false;

    async function loadRelay() {
      setStatus('loading');
      const leaderboard = await getCampaignLeaderboard({ user, campaignId: id });
      const DEFAULT_ROUTE = { startLocation: 'Lũng Cú', endLocation: 'Cà Mau' };
      const model = buildRelayModel({
        campaignId: id,
        campaignName: campaign.name,
        leaderboard,
        currentUserId: user?.user?.id || user?.user?.stravaId || user?.user?.strava_id,
        route: resolveRoute(campaign, DEFAULT_ROUTE),
      });

      const rawCheckpoints = resolveCheckpoints(campaign, demoCheckpoints);
      const resolvedMedals  = resolveMedals(campaign, demoMedals);

      const journey = buildMapJourney({
        relay: model,
        checkpoints: rawCheckpoints,
        medals: resolvedMedals,
        scaleCheckpointsToTotal: true,
      });
      if (!ignore) {
        setRelay(journey);
        setStatus('ready');
      }
    }

    loadRelay().catch(() => setStatus('error'));
    return () => {
      ignore = true;
    };
  }, [campaign.name, id, user]);

  if (status === 'loading') return <div className="htv-state">Đang dựng bản đồ hành trình...</div>;
  if (status === 'error') return <div className="htv-state">Không tải được bản đồ. Vui lòng thử lại.</div>;
  return (
    <section className="relay-page map-page">
      <nav className="map-subnav" aria-label="Campaign map navigation">
        <Link to={`/campaign/${name}/${id}`}>Tổng quan campaign</Link>
        <span>Bản đồ hành trình</span>
      </nav>

      <header className="map-page-header map-page-header-compact">
        <div>
          <p className="htv-muted">Bản đồ hành trình</p>
          <h1>{relay.campaignName}</h1>
        </div>
        <span className="map-header-progress">
          {Math.round(relay.progressRatio * 100)}%
        </span>
      </header>

      {!isLoggedIn && (
        <p className="relay-demo-note">
          Bạn vẫn có thể xem bản đồ khi chưa đăng ký KPI. Đăng nhập Strava khi cần đồng bộ dữ liệu thật.
        </p>
      )}

      <CampaignRegistrationPanel
        campaign={campaign}
        campaignId={id}
        campaignName={campaign.name}
        compact
      />

      <ChallengeStrip journey={relay} />
      <RelayRoute relay={relay} />

      <div className="map-shell">
        <StreetJourneyMap journey={relay} onSelectCheckpoint={setSelectedCheckpoint} />
        <MapOverlays journey={relay} />
      </div>

      <JourneySections journey={relay} onSelectCheckpoint={setSelectedCheckpoint} />

      <CheckpointDrawer
        checkpoint={selectedCheckpoint}
        open={Boolean(selectedCheckpoint)}
        onClose={() => setSelectedCheckpoint(null)}
      />
    </section>
  );
}
